import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiUser, FiEye, FiTrash2, FiToggleLeft, FiToggleRight, FiShield, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import {
  fetchUsers,
  updateUserStatus,
  updateUserRole,
  deleteUser,
  verifyUser,
  resetSuccess,
} from '../../store/slices/usersSlice';
import { DataTable, SearchBox, Pagination, Modal, ConfirmDialog } from '../../components/Common';
import { getStatusBadgeClass, getStatusText, formatDate, getImageUrl } from '../../utils/helpers';

const Users = () => {
  const dispatch = useDispatch();
  const { users, totalUsers, totalPages, isLoading, success, error } = useSelector(
    (state) => state.users
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [verifyNote, setVerifyNote] = useState('');
  const [verifyTarget, setVerifyTarget] = useState(null);

  useEffect(() => {
    const params = { page: currentPage, limit: 10 };
    if (searchTerm) params.search = searchTerm;
    if (filterRole) params.role = filterRole;
    dispatch(fetchUsers(params));
  }, [dispatch, currentPage, searchTerm, filterRole]);

  useEffect(() => {
    if (success) {
      dispatch(resetSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setIsDetailOpen(true);
  };

  const handleToggleStatus = async (user) => {
    const newStatus = !user.isActive;
    const result = await dispatch(updateUserStatus({ id: user._id, isActive: newStatus }));
    if (updateUserStatus.fulfilled.match(result)) {
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    }
  };

  const handleToggleRole = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    const result = await dispatch(updateUserRole({ id: user._id, role: newRole }));
    if (updateUserRole.fulfilled.match(result)) {
      toast.success(`User role changed to ${newRole}`);
    }
  };

  const handleDelete = async () => {
    if (selectedUser) {
      const result = await dispatch(deleteUser(selectedUser._id));
      if (deleteUser.fulfilled.match(result)) {
        toast.success('User deleted successfully');
        setIsDeleteOpen(false);
        setSelectedUser(null);
      }
    }
  };

  const handleOpenVerify = (user) => {
    setVerifyTarget(user);
    setVerifyNote(user.verification?.note || '');
    setIsVerifyOpen(true);
  };

  const handleVerify = async (isVerified) => {
    if (!verifyTarget) return;
    const result = await dispatch(verifyUser({ id: verifyTarget._id, isVerified, note: verifyNote }));
    if (verifyUser.fulfilled.match(result)) {
      toast.success(isVerified ? 'User verified successfully' : 'Verification removed');
      setIsVerifyOpen(false);
      setVerifyTarget(null);
      setVerifyNote('');
      // Update selectedUser if detail modal is open
      if (selectedUser && selectedUser._id === verifyTarget._id) {
        setSelectedUser(result.payload.user);
      }
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const columns = [
    {
      header: 'User',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {row.photos && row.photos[0] ? (
            <img
              src={getImageUrl(row.photos[0])}
              alt={row.name || (row.firstName ? `${row.firstName} ${row.lastName || ''}`.trim() : '') || row.mobile}
              className="table-image"
              style={{ borderRadius: '50%' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
              }}
            >
              <FiUser />
            </div>
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{row.name || (row.firstName ? `${row.firstName} ${row.lastName || ''}`.trim() : null) || 'N/A'}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.mobile}</div>
          </div>
        </div>
      ),
    },
    {
      header: 'Role',
      render: (row) => (
        <span
          className={`badge ${row.role === 'admin' ? 'badge-info' : 'badge-secondary'}`}
          style={{ textTransform: 'capitalize' }}
        >
          {row.role || 'user'}
        </span>
      ),
    },
    {
      header: 'Location',
      render: (row) => {
        const city = row.cityId?.name;
        const state = row.stateId?.name;
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return '-';
      },
    },
    {
      header: 'Profile',
      render: (row) => (
        <span className={`badge ${row.isProfileComplete ? 'badge-success' : 'badge-warning'}`}>
          {row.isProfileComplete ? 'Complete' : 'Incomplete'}
        </span>
      ),
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`badge ${getStatusBadgeClass(row.isActive)}`}>
          {getStatusText(row.isActive)}
        </span>
      ),
    },
    {
      header: 'Verified',
      render: (row) => (
        <span
          className={`badge ${row.verification?.isVerified ? 'badge-success' : 'badge-warning'}`}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', width: 'fit-content' }}
        >
          {row.verification?.isVerified ? <FiCheckCircle size={12} /> : <FiXCircle size={12} />}
          {row.verification?.isVerified ? 'Verified' : 'Unverified'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="table-actions">
          <button
            className="btn btn-secondary btn-icon"
            onClick={() => handleViewDetails(row)}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
            className={`btn btn-icon ${row.isActive ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => handleToggleStatus(row)}
            title={row.isActive ? 'Deactivate' : 'Activate'}
          >
            {row.isActive ? <FiToggleRight /> : <FiToggleLeft />}
          </button>
          <button
            className={`btn btn-icon ${row.role === 'admin' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleToggleRole(row)}
            title={row.role === 'admin' ? 'Make User' : 'Make Admin'}
          >
            <FiShield />
          </button>
          <button
            className={`btn btn-icon ${row.verification?.isVerified ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => handleOpenVerify(row)}
            title={row.verification?.isVerified ? 'Verified — Click to manage' : 'Verify User'}
          >
            <FiCheckCircle />
          </button>
          <button
            className="btn btn-danger btn-icon"
            onClick={() => {
              setSelectedUser(row);
              setIsDeleteOpen(true);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      ),
      style: { width: '180px' },
    },
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <h3 className="card-title">Users ({totalUsers})</h3>
            <SearchBox
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search by name or mobile..."
            />
            <select
              className="form-control form-select"
              style={{ width: '150px' }}
              value={filterRole}
              onChange={(e) => {
                setFilterRole(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <DataTable
            columns={columns}
            data={users}
            isLoading={isLoading}
            emptyMessage="No users found"
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <Modal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        title="User Details"
      >
        {selectedUser && (
          <div style={{ maxHeight: '80vh', overflowY: 'auto', paddingRight: '4px' }}>

            {/* ── Profile Header ── */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              {selectedUser.profilePhoto || (selectedUser.additionalPhotos && selectedUser.additionalPhotos[0]) ? (
                <img
                  src={getImageUrl(selectedUser.profilePhoto || selectedUser.additionalPhotos[0])}
                  alt={selectedUser.name || selectedUser.fullName}
                  style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #e5e7eb' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: '40px', margin: '0 auto' }}>
                  <FiUser />
                </div>
              )}
              <h3 style={{ marginTop: '12px', marginBottom: '6px' }}>
                {selectedUser.firstName && selectedUser.lastName
                  ? `${selectedUser.firstName} ${selectedUser.lastName}`
                  : selectedUser.name || selectedUser.fullName || 'N/A'}
              </h3>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span className={`badge ${selectedUser.role === 'admin' ? 'badge-info' : 'badge-secondary'}`} style={{ textTransform: 'capitalize' }}>{selectedUser.role || 'user'}</span>
                <span className={`badge ${selectedUser.isActive ? 'badge-success' : 'badge-danger'}`}>{selectedUser.isActive ? 'Active' : 'Inactive'}</span>
                <span className={`badge ${selectedUser.isPremium ? 'badge-warning' : 'badge-secondary'}`}>{selectedUser.isPremium ? '★ Premium' : 'Free'}</span>
                <span className={`badge ${selectedUser.verification?.isVerified ? 'badge-success' : 'badge-warning'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {selectedUser.verification?.isVerified ? <FiCheckCircle size={11} /> : <FiXCircle size={11} />}
                  {selectedUser.verification?.isVerified ? 'Verified' : 'Unverified'}
                </span>
              </div>
            </div>

            {/* ── Section Helper ── */}
            {(() => {
              const Section = ({ title, children }) => (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #e5e7eb' }}>
                    {title}
                  </div>
                  {children}
                </div>
              );
              const Grid = ({ children }) => (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>{children}</div>
              );
              const Field = ({ label, value, full }) => (
                <div style={full ? { gridColumn: '1 / -1' } : {}}>
                  <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600, marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '13px', color: '#111827' }}>{value || '-'}</div>
                </div>
              );

              const u = selectedUser;
              const dob = u.dateOfBirth ? new Date(u.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

              return (
                <>
                  {/* ── Account Info ── */}
                  <Section title="Account Info">
                    <Grid>
                      <Field label="Mobile" value={u.mobile} />
                      <Field label="Profile For" value={u.profileFor} />
                      <Field label="Looking For" value={u.lookingFor} />
                      <Field label="Profile Complete" value={
                        <span className={`badge ${u.isProfileComplete ? 'badge-success' : 'badge-warning'}`}>
                          {u.isProfileComplete ? 'Yes' : 'No'}
                        </span>
                      } />
                      <Field label="Joined" value={formatDate(u.createdAt)} />
                      <Field label="Last Updated" value={formatDate(u.updatedAt)} />
                    </Grid>
                  </Section>

                  {/* ── Basic Info ── */}
                  <Section title="Basic Info">
                    <Grid>
                      <Field label="First Name" value={u.firstName} />
                      <Field label="Last Name" value={u.lastName} />
                      <Field label="Date of Birth" value={dob} />
                      <Field label="Gender" value={u.gender} />
                      <Field label="Religion" value={u.religion} />
                      <Field label="Caste" value={u.caste} />
                      <Field label="Mother Tongue" value={u.motherTongue} />
                      <Field label="Education" value={u.education} />
                    </Grid>
                  </Section>

                  {/* ── Lifestyle ── */}
                  <Section title="Lifestyle">
                    <Grid>
                      <Field label="Height" value={u.height} />
                      <Field label="Marital Status" value={u.maritalStatus} />
                      <Field label="Diet" value={u.diet} />
                    </Grid>
                  </Section>

                  {/* ── Career & Location ── */}
                  <Section title="Career & Location">
                    <Grid>
                      <Field label="Profession" value={u.profession} />
                      <Field label="Employed In" value={u.employedIn} />
                      <Field label="Annual Income" value={u.annualIncome} />
                      <Field label="State" value={u.stateId?.name || u.state} />
                      <Field label="City" value={u.cityId?.name || u.city} />
                    </Grid>
                  </Section>

                  {/* ── Family ── */}
                  <Section title="Family">
                    <Grid>
                      <Field label="Family Type" value={u.familyType} />
                      <Field label="Family Values" value={u.familyValues} />
                      <Field label="Father's Occupation" value={u.fathersOccupation} />
                      <Field label="Mother's Occupation" value={u.mothersOccupation} />
                    </Grid>
                  </Section>

                  {/* ── About Me ── */}
                  {u.aboutMe && (
                    <Section title="About Me">
                      <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', background: '#f9fafb', padding: '10px 12px', borderRadius: '6px' }}>
                        {u.aboutMe}
                      </div>
                    </Section>
                  )}

                  {/* ── Photos ── */}
                  {(u.profilePhoto || (u.additionalPhotos && u.additionalPhotos.length > 0)) && (
                    <Section title="Photos">
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {u.profilePhoto && (
                          <div style={{ position: 'relative' }}>
                            <img src={getImageUrl(u.profilePhoto)} alt="Profile" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #6366f1' }} onError={(e) => { e.target.style.display = 'none'; }} />
                            <span style={{ position: 'absolute', bottom: '2px', left: '2px', background: '#6366f1', color: '#fff', fontSize: '9px', padding: '1px 4px', borderRadius: '4px' }}>Main</span>
                          </div>
                        )}
                        {(u.additionalPhotos || []).map((photo, i) => (
                          <img key={i} src={getImageUrl(photo)} alt={`Photo ${i + 1}`} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e5e7eb' }} onError={(e) => { e.target.style.display = 'none'; }} />
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Siblings ── */}
                  <Section title={`Siblings (${(u.siblings || []).length})`}>
                    {u.siblings && u.siblings.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f3f4f6' }}>
                              {['No.', 'Name', 'Gender', 'Age', 'Marital Status'].map((h) => (
                                <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '11px', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {u.siblings.map((s, i) => (
                              <tr key={s._id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '7px 10px' }}>{s.no}</td>
                                <td style={{ padding: '7px 10px' }}>{s.name || '-'}</td>
                                <td style={{ padding: '7px 10px', textTransform: 'capitalize' }}>{s.gender}</td>
                                <td style={{ padding: '7px 10px' }}>{s.age}</td>
                                <td style={{ padding: '7px 10px' }}>{s.maritalStatus}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>No siblings added</p>
                    )}
                  </Section>

                  {/* ── Verification ── */}
                  <Section title="Verification">
                    <div style={{ background: u.verification?.isVerified ? '#f0fdf4' : '#fffbeb', border: `1px solid ${u.verification?.isVerified ? '#bbf7d0' : '#fde68a'}`, borderRadius: '8px', padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: u.verification?.isVerified ? '10px' : 0 }}>
                        {u.verification?.isVerified
                          ? <FiCheckCircle size={18} color="#16a34a" />
                          : <FiXCircle size={18} color="#d97706" />}
                        <span style={{ fontWeight: 600, color: u.verification?.isVerified ? '#166534' : '#92400e', fontSize: '14px' }}>
                          {u.verification?.isVerified ? 'Profile Verified' : 'Not Verified'}
                        </span>
                      </div>
                      {u.verification?.isVerified && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: '#166534' }}>
                          <div><span style={{ fontWeight: 600 }}>Verified on:</span> {u.verification.verifiedAt ? formatDate(u.verification.verifiedAt) : '-'}</div>
                          <div><span style={{ fontWeight: 600 }}>Verified by:</span> {u.verification.verifiedBy?.name || u.verification.verifiedBy?.mobile || 'Admin'}</div>
                          {u.verification.note && (
                            <div style={{ gridColumn: '1 / -1' }}><span style={{ fontWeight: 600 }}>Note:</span> {u.verification.note}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </Section>

                  {/* ── Verify Action ── */}
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                    <button
                      className={`btn ${u.verification?.isVerified ? 'btn-secondary' : 'btn-success'}`}
                      style={{ width: '100%' }}
                      onClick={() => { setIsDetailOpen(false); handleOpenVerify(u); }}
                    >
                      <FiCheckCircle style={{ marginRight: '8px' }} />
                      {u.verification?.isVerified ? 'Manage Verification' : 'Verify This User'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${selectedUser?.name || (selectedUser?.firstName ? `${selectedUser.firstName} ${selectedUser.lastName || ''}`.trim() : null) || selectedUser?.mobile}"? This action cannot be undone.`}
        isLoading={isLoading}
      />

      {/* Verification Modal */}
      <Modal
        isOpen={isVerifyOpen}
        onClose={() => { setIsVerifyOpen(false); setVerifyNote(''); setVerifyTarget(null); }}
        title="Manage User Verification"
      >
        {verifyTarget && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div
                style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: '#e5e7eb', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#6b7280', flexShrink: 0,
                }}
              >
                <FiUser />
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 600 }}>{verifyTarget.name || (verifyTarget.firstName ? `${verifyTarget.firstName} ${verifyTarget.lastName || ''}`.trim() : null) || 'N/A'}</p>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{verifyTarget.mobile}</p>
              </div>
              <span
                className={`badge ${verifyTarget.verification?.isVerified ? 'badge-success' : 'badge-warning'}`}
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
              >
                {verifyTarget.verification?.isVerified ? <FiCheckCircle size={12} /> : <FiXCircle size={12} />}
                {verifyTarget.verification?.isVerified ? 'Verified' : 'Unverified'}
              </span>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label">Admin Note (Optional)</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="e.g. Documents verified via Aadhaar and PAN."
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn-success"
                style={{ flex: 1 }}
                onClick={() => handleVerify(true)}
                disabled={isLoading}
              >
                <FiCheckCircle style={{ marginRight: '6px' }} />
                {isLoading ? 'Verifying...' : 'Verify User'}
              </button>
              {verifyTarget.verification?.isVerified && (
                <button
                  className="btn btn-danger"
                  style={{ flex: 1 }}
                  onClick={() => handleVerify(false)}
                  disabled={isLoading}
                >
                  <FiXCircle style={{ marginRight: '6px' }} />
                  {isLoading ? 'Removing...' : 'Remove Verification'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Users;
